import React, { useMemo, useState, useRef, useEffect } from "react";
import { ChevronDown, Search, AlertTriangle } from "lucide-react";
import COUNTRIES from "@/lib/countryCodes";
import { useLang } from "@/context/LanguageContext";

export const MIN_LOCAL_DIGITS = 8;
// E.164 allows up to 15 digits including the country code; keep the national part generous.
const MAX_LOCAL_DIGITS = 14;
// Arabic-Indic / Persian digits are converted, never discarded.
const latinDigits = (s) => String(s || "")
  .replace(/[\u0660-\u0669]/g, (d) => String(d.charCodeAt(0) - 0x0660))
  .replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06F0));
const onlyDigits = (s) => latinDigits(s).replace(/\D/g, "").slice(0, MAX_LOCAL_DIGITS);

// Parse the composed "+DIAL DIGITS" back into its two parts.
export function parsePhoneValue(v) {
  if (!v) return { dial: "+971", digits: "" };
  const m = /^(\+\d{1,4})\s?(.*)$/.exec(v.trim());
  if (m) return { dial: m[1], digits: onlyDigits(m[2]) };
  return { dial: "+971", digits: onlyDigits(v) };
}

export function isPhoneValid(v) {
  return parsePhoneValue(v).digits.length >= MIN_LOCAL_DIGITS;
}

export default function PhoneWithCountry({ label, testid, value, onChange, required = false, error }) {
  const { lang } = useLang();
  const isAR = lang === "ar";
  const parsed = useMemo(() => parsePhoneValue(value), [value]);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [touched, setTouched] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const tooShort = touched && parsed.digits.length > 0 && parsed.digits.length < MIN_LOCAL_DIGITS;
  const warningLine = isAR
    ? `يبدو أن الرقم غير مكتمل (${parsed.digits.length}/${MIN_LOCAL_DIGITS} أرقام). تأكد من إضافة الرقم المحلي كاملاً بعد رمز الدولة.`
    : `That phone number looks incomplete (${parsed.digits.length}/${MIN_LOCAL_DIGITS} digits). Please enter the full local number after the country code.`;

  const emit = (dial, digits) => {
    const clean = onlyDigits(digits);
    onChange && onChange({ target: { value: `${dial} ${clean}`.trim() } });
  };

  const selected = COUNTRIES.find((c) => c.dial === parsed.dial) || COUNTRIES[0];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRIES;
    return COUNTRIES.filter((c) => c.name.toLowerCase().includes(q) || (c.name_ar || "").includes(q) || c.dial.includes(q) || c.iso.toLowerCase() === q);
  }, [search]);

  return (
    <div className="block relative" ref={wrapRef}>
      <span className="font-mono-brand text-propertic-muted text-[10px] tracking-[0.14em] uppercase">
        {label}
        {required ? <span className="text-red-500 ms-1">*</span> : null}
      </span>
      {/* Phone rows are LTR everywhere , picker on the LEFT, digits on the RIGHT.
          This is the international convention (+CC then number) and stops the
          "+971" bleeding into "971+" in RTL contexts. */}
      <div dir="ltr" className={`mt-2 flex items-stretch gap-2 border-b ${error || tooShort ? "border-amber-500" : "border-propertic-ink/20"} focus-within:border-propertic-gold transition-colors`}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid={`${testid}-cc-toggle`}
          className="flex items-center gap-1.5 pr-2 pb-1 text-propertic-ink hover:text-propertic-navy"
        >
          <img src={`https://flagcdn.com/w40/${selected.iso.toLowerCase()}.png`} alt={selected.name} width="20" height="15" className="w-5 h-auto rounded-[2px] object-cover shadow-sm" draggable={false} />
          <span dir="ltr" className="font-mono-brand text-[13px] tabular">{selected.dial}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <input
          type="tel"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={24}
          dir="ltr"
          value={parsed.digits}
          onChange={(e) => {
            // A pasted full international number (+44 … / 0044 …) sets the dial code.
            const raw = latinDigits(e.target.value).trim();
            const intl = /^(?:\+|00)(\d{1,4})/.exec(raw.replace(/[\s-]/g, ""));
            if (intl) {
              const all = raw.replace(/\D/g, "").replace(/^00/, "");
              const c = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)
                .find((x) => all.startsWith(x.dial.replace("+", "")));
              if (c) { emit(c.dial, all.slice(c.dial.length - 1)); return; }
            }
            emit(parsed.dial, e.target.value);
          }}
          onBlur={() => setTouched(true)}
          placeholder="055 1234567"
          data-testid={testid}
          className="flex-1 bg-transparent outline-none py-2 text-propertic-ink text-base tabular text-left"
        />
      </div>
      {tooShort && (
        <p data-testid={`${testid}-warning`} className="mt-1.5 flex items-start gap-1.5 text-[11px] text-amber-600 leading-snug" dir={isAR ? "rtl" : "ltr"}>
          <AlertTriangle className="w-3 h-3 mt-[2px] shrink-0" />
          <span>{warningLine}</span>
        </p>
      )}
      {open && (
        <div data-testid={`${testid}-cc-menu`} className="absolute z-50 mt-1 start-0 min-w-[280px] max-w-[340px] max-h-72 overflow-y-auto bg-propertic-paper-2 rounded-2xl border hairline shadow-xl py-2">
          <div className="px-3 pb-2 sticky top-0 bg-propertic-paper-2">
            <div className="flex items-center gap-2 border-b border-propertic-ink/15 pb-1.5">
              <Search className="w-3.5 h-3.5 text-propertic-muted" />
              <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)} placeholder={isAR ? "ابحث عن دولة أو رمز…" : "Search country or dial code"} className="w-full bg-transparent outline-none text-sm text-propertic-ink" />
            </div>
          </div>
          {filtered.map((c) => (
            <button
              key={c.iso}
              type="button"
              onClick={() => { emit(c.dial, parsed.digits); setOpen(false); setSearch(""); }}
              data-testid={`${testid}-cc-${c.iso}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-start transition-colors ${c.dial === selected.dial ? "bg-propertic-sand/60" : "hover:bg-propertic-sand/40"}`}
            >
              <img src={`https://flagcdn.com/w40/${c.iso.toLowerCase()}.png`} alt="" width="18" height="14" className="w-[18px] h-auto rounded-[2px] object-cover" draggable={false} />
              <span className="text-propertic-ink flex-1 truncate">{isAR ? (c.name_ar || c.name) : c.name}</span>
              <span dir="ltr" className="font-mono-brand text-propertic-muted tabular text-[11px]">{c.dial}</span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="px-3 py-4 text-center text-xs text-propertic-muted">{isAR ? "لا توجد نتائج" : "No match"}</p>
          )}
        </div>
      )}
    </div>
  );
}
